import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Settings, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { HomyLogo } from "@/components/brand/HomyLogo";
import { AppNavSections } from "./AppNavSections";

/**
 * Sidebar: left rail with grouped nav; collapsed mode shows icon-only leaves with native titles.
 */
export function AppSidebar({
  collapsed,
  onItemClick,
  onToggleCollapse,
  navGroups,
  openGroups,
  onToggleGroup,
}) {
  const { t } = useTranslation("common");
  const location = useLocation();

  return (
    <aside
      className={cn(
        "hidden md:flex h-full flex-col flex-shrink-0 border-r border-sidebar-border bg-sidebar text-sidebar-foreground z-30 transition-all duration-300 overflow-x-hidden",
        collapsed ? "w-20" : "w-64",
      )}
    >
      <div
        className={cn(
          "h-14 flex items-center border-b border-sidebar-border shrink-0 transition-all duration-300",
          collapsed ? "justify-center px-0" : "px-6",
        )}
      >
        <HomyLogo collapsed={collapsed} />
      </div>

      <nav
        className="flex-1 py-6 px-3 flex flex-col gap-1 overflow-y-auto overflow-x-hidden custom-scrollbar"
        aria-label={t("nav.mainNavigation")}
      >
        <AppNavSections
          groups={navGroups}
          collapsed={collapsed}
          groupPresentation={collapsed ? null : "desktopExpanded"}
          openGroups={openGroups}
          onToggleGroup={onToggleGroup}
          pathname={location.pathname}
          onNavigate={onItemClick}
        />
      </nav>

      <div
        className={cn(
          "p-4 border-t border-sidebar-border flex flex-col gap-1 transition-all duration-300",
          collapsed && "px-3",
        )}
      >
        <button
          type="button"
          onClick={() => onItemClick("/settings")}
          aria-label={t("nav.settings")}
          aria-current={location.pathname === "/settings" ? "page" : undefined}
          title={collapsed ? t("nav.settings") : undefined}
          className={cn(
            "flex items-center rounded-xl transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar",
            collapsed
              ? "justify-center w-11 h-11 md:w-11 md:min-h-11 mx-auto px-0"
              : "w-full min-h-[44px] md:min-h-10 gap-3 px-3 py-2.5",
            location.pathname === "/settings"
              ? "bg-primary/12 text-primary font-medium"
              : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
          )}
        >
          <Settings
            size={collapsed ? 24 : 18}
            className={cn("shrink-0", collapsed && "h-6 w-6")}
            aria-hidden
          />
          {!collapsed && (
            <span className="text-sm font-semibold tracking-tight">
              {t("nav.settings")}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={onToggleCollapse}
          title={collapsed ? t("expandSidebar") : undefined}
          className={cn(
            "flex items-center text-muted-foreground/80 hover:text-foreground hover:bg-muted/60 transition-colors cursor-pointer rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar",
            collapsed
              ? "justify-center w-11 h-11 md:w-11 md:min-h-11 mx-auto px-0"
              : "w-full min-h-[44px] md:min-h-10 gap-3 px-3 py-2.5",
          )}
          aria-expanded={!collapsed}
          aria-label={collapsed ? t("expandSidebar") : t("collapseSidebar")}
        >
          {collapsed ? (
            <ChevronRight size={24} className="h-6 w-6 shrink-0" />
          ) : (
            <ChevronLeft size={18} className="shrink-0" />
          )}
          {!collapsed && (
            <span className="text-sm font-semibold tracking-tight">
              {t("collapse")}
            </span>
          )}
        </button>
      </div>
    </aside>
  );
}
